import React, { useEffect, useLayoutEffect, useMemo, useRef } from 'react';

import { barLayout } from './model/bar-layout';
import { useGanttState } from './state/use-gantt-state';
import { cssVars } from './view/css-vars';
import { gridBackground } from './view/grid-background';
import { HoverGuide } from './view/hover-guide';
import { ROW_HEIGHT_PX, RowList } from './view/row-list';
import { HEADER_ROW_INDEX, TimelineHeader } from './view/timeline-header';
import { useScrollMetrics } from './view/use-scroll-metrics';
import { extensionSideFor, visibleDayWindow } from './view/visible-days';
import { dayOfWeekOfEpochDay } from '../../core/services/plain-date';
import styles from './gantt-chart.module.scss';

import type { GanttConfig, GanttItem, GanttPatch, GanttRenderers, Row } from './types';

/** Matches `--gantt-sidebar-width`, which this component writes from here. */
const SIDEBAR_PX = 240;
const OVERSCAN_DAYS = 10;

export type GanttChartProps = {
  items: GanttItem[];
  onCommit?: (patches: GanttPatch[]) => Promise<void> | void;
  config?: GanttConfig;
  renderers?: GanttRenderers;
  /** Accent for depth-0 group bars. Kept for source compatibility with the old props. */
  primaryColor?: string;
  /** Accent for depth-1 group bars. */
  secondaryColor?: string;
};

/**
 * ONE scroll container, `.scroller`. The timeline header's row group is
 * `position: sticky; top: 0` and each row's header cell is `position: sticky; left: 0`, so the
 * browser keeps both in place and nothing mirrors `scrollLeft` by hand, the way the chart this
 * replaced had to with a second, hand-synchronised header container.
 *
 * That only holds while every box between `.scroller` and a sticky descendant is as wide as the
 * scrollable content: a sticky element is clamped to its containing block, so a row box the
 * width of the scrollport would carry its own sticky header out of view as soon as the user
 * scrolled past one viewport. `.canvas` is what fixes the width for everything below it, at
 * `--gantt-sidebar-width + --gantt-range-width`, and `--gantt-range-width` is written here.
 */
export const GanttChart: React.FC<GanttChartProps> = ({
  items,
  onCommit,
  config,
  renderers,
  primaryColor,
  secondaryColor,
}) => {
  const state = useGanttState(items, config);
  const scrollRef = useRef<HTMLDivElement | null>(null);
  // `.canvas`'s own ref — deliberately not `scrollRef` (`.scroller`, the viewport). `HoverGuide`
  // reads `getBoundingClientRect()` off this element directly; see its own doc-comment for why
  // that already accounts for horizontal scroll without needing `scrollLeft` added back by hand.
  const canvasRef = useRef<HTMLDivElement | null>(null);
  const { scrollLeft, viewportWidth } = useScrollMetrics(scrollRef);

  const rangeDays = state.range.endDay - state.range.startDay + 1;
  const rangeWidth = rangeDays * state.scale.pxPerDay;

  const dayWindow = useMemo(
    () =>
      visibleDayWindow({
        scrollLeft,
        viewportWidth,
        sidebarWidth: SIDEBAR_PX,
        pxPerDay: state.scale.pxPerDay,
        range: state.range,
        overscanDays: OVERSCAN_DAYS,
      }),
    [scrollLeft, viewportWidth, state.scale.pxPerDay, state.range],
  );

  // Centre today, once. The old module did this with `getElementById` plus a two-second timeout
  // guard whose own comment flagged it as a TODO; the position is derivable from the scale, so
  // it lands on the first frame the viewport has been measured on.
  //
  // Mount-only BY DESIGN, and the deliberate exception to Phase 2's "re-seeding on input change"
  // gate (Task 16, `use-gantt-state.ts`). That gate landed as TWO mechanisms, not one:
  // `rangeOverride` resets on `rangeSignature`, a primitive derived from the covered extent, once
  // `extend` had frozen it against a window that stopped matching the data; separately, ids in
  // `defaultCollapsed` are re-applied to `uncontrolled` as they appear, because the space route
  // recomputes `defaultCollapsed` from `items` on every items change
  // (`multi-projects-calendar.page.tsx:241`) and `useGanttState`'s own `useState` only reads its
  // initializer once — without that second mechanism a project arriving after mount would render
  // expanded forever, not merely late.
  //
  // Recentring here is different in kind, not just left out of that gate: it is intentionally
  // mount-only forever. Re-centring on today because a teammate edited a step somewhere would
  // yank the viewport out from under whoever is reading it — scroll position belongs to the user
  // from the first frame on, and no future task should key this off `rangeSignature` or
  // `defaultCollapsed` the way the other two do.
  const hasCentred = useRef(false);
  useLayoutEffect(() => {
    if (hasCentred.current || !viewportWidth || !scrollRef.current) return;

    const todayX = state.scale.xOf(state.todayDay, state.range.startDay);
    scrollRef.current.scrollLeft = todayX - (viewportWidth - SIDEBAR_PX) / 2;
    hasCentred.current = true;
  }, [viewportWidth, state.scale, state.todayDay, state.range.startDay]);

  // Moving the range's START moves every column with it, so hold the viewport still over the same
  // days. This runs before paint, and before the passive effect below re-reads the edges.
  //
  // I2: both directions, not just growth. The original `added > 0` covered `extend('start')`
  // prepending columns and nothing else, on the reasoning that the start only ever grows earlier.
  // It also SHRINKS — whenever `use-gantt-state.ts`'s re-seed gate drops a `rangeOverride` the
  // user had extended — and an uncompensated shrink jumps the viewport by up to the whole
  // extension, or has the browser clamp `scrollLeft` when it now exceeds the content width. The
  // arithmetic is already symmetric: `added` is negative for a shrink, so the same line holds the
  // same days under the same pixels either way.
  const previousStartDay = useRef(state.range.startDay);
  useLayoutEffect(() => {
    const added = previousStartDay.current - state.range.startDay;
    previousStartDay.current = state.range.startDay;

    if (added !== 0 && scrollRef.current) {
      scrollRef.current.scrollLeft += added * state.scale.pxPerDay;
    }
  }, [state.range.startDay, state.scale.pxPerDay]);

  // Grow the range when the viewport approaches either edge. Replaces the two
  // IntersectionObservers on header month elements.
  const lastExtension = useRef<string | null>(null);
  useEffect(() => {
    const element = scrollRef.current;
    if (!element || !viewportWidth) return;

    // Measured from the LIVE scroll position rather than from `scrollLeft` above.
    // `useScrollMetrics` is a frame behind the DOM by construction, and both layout effects
    // above write `scrollLeft` synchronously — judging the edges against the stale value reads
    // as "pinned to the start edge" right after mount and grows the range for nothing.
    const live = visibleDayWindow({
      scrollLeft: element.scrollLeft,
      viewportWidth,
      sidebarWidth: SIDEBAR_PX,
      pxPerDay: state.scale.pxPerDay,
      range: state.range,
      overscanDays: OVERSCAN_DAYS,
    });
    const side = extensionSideFor(live, state.range);

    if (!side) {
      lastExtension.current = null;

      return;
    }

    // One extension per scroll position. Extending the start prepends columns and the effect
    // above compensates, which moves the viewport off the edge and ends the sequence. Should
    // that write ever fail to land — the element is not scrollable, the value is clamped — the
    // next run would see the same edge again and extend until `extendRange` reaches its cap.
    // Remembering the position we last extended from bounds that at one; a run that no longer
    // needs an extension clears it again.
    const key = `${side}@${element.scrollLeft}`;
    if (lastExtension.current === key) return;

    lastExtension.current = key;
    state.extend(side);
    // `state.extend` and `state.scale` are stable for a given zoom, and `state.range` is read
    // through its two primitives — depending on the objects would re-run this on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollLeft, viewportWidth, state.range.startDay, state.range.endDay, state.scale.pxPerDay]);

  // The seven-column lead-in matches the old `- 336 // 48px * 7` at
  // `components/gantt-chart/utils/utils.ts:31` — scrolling the bar flush to the edge it just
  // fell off would immediately re-trigger the same indicator.
  const onScrollToRow = (row: Row) => {
    if (!row.span || !scrollRef.current) return;

    const x = barLayout(row.span, state.scale, state.range.startDay).x;
    scrollRef.current.scrollTo({ left: x - 7 * state.scale.pxPerDay, behavior: 'smooth' });
  };

  // `pxPerDay` belongs in the deps as much as it belongs in the call: leave it out and every
  // column, week separator and grid line stays at the previous zoom's width while the bars and
  // ticks move.
  const grid = useMemo(
    () =>
      gridBackground(
        dayOfWeekOfEpochDay(state.range.startDay),
        state.workingWeek,
        state.scale.pxPerDay,
      ),
    [state.range.startDay, state.workingWeek, state.scale.pxPerDay],
  );

  const todayX = state.scale.xOf(state.todayDay, state.range.startDay);
  const isTodayInRange =
    state.todayDay >= state.range.startDay && state.todayDay <= state.range.endDay;

  return (
    <div
      className={styles['root']}
      data-zoom={state.scale.zoom}
      style={cssVars({
        '--gantt-px-per-day': `${state.scale.pxPerDay}px`,
        '--gantt-range-width': `${rangeWidth}px`,
        '--gantt-sidebar-width': `${SIDEBAR_PX}px`,
        // The virtualizer's stride, and the ONLY row dimension JS writes. It goes on `.root`
        // with every other JS-owned property: `RowList` used to write `--gantt-row-height` and
        // `--gantt-row-gap` inline on `.rows`, a DESCENDANT, where an inline value shadows
        // anything set above it — which made the two dimensions spec §6.1 calls customizable the
        // only two a stylesheet could not change. The gap is now pure CSS and the height derives
        // from stride minus gap, so an override moves the painted row inside a stride the
        // virtualizer still agrees with. See `gantt-chart.module.scss`.
        '--gantt-row-stride': `${ROW_HEIGHT_PX}px`,
        ...(primaryColor ? { '--gantt-accent-1': primaryColor } : {}),
        ...(secondaryColor ? { '--gantt-accent-2': secondaryColor } : {}),
      })}
    >
      <div className={styles['scroller']} ref={scrollRef}>
        <div
          className={styles['canvas']}
          ref={canvasRef}
          role="grid"
          aria-label="Gantt chart"
          // The timeline header is a row of this grid and occupies index 1, so it is counted
          // here and body rows are offset past it. Counting only the body rows while numbering
          // them from 1 had the header row and the first body row both claiming index 1.
          aria-rowcount={state.rows.length + HEADER_ROW_INDEX}
          style={{
            // No `minHeight` here: `.canvas` takes `min-height: 100%` from the stylesheet so the
            // day grid fills the scrollport, and an inline value would beat it.
            backgroundImage: grid.background,
            backgroundSize: grid.backgroundSize,
            // The first and third layers are the day columns and the day grid line, both of
            // which start at the timeline origin; the second tiles the full width anyway.
            backgroundPosition: `${SIDEBAR_PX}px 0, 0 0, ${SIDEBAR_PX}px 0`,
          }}
        >
          {/* C1 — DOM ORDER IS PART OF THIS COMPONENT'S CONTRACT, NOT A FREE CHOICE. `HoverGuide`
              renders FIRST, ahead of the header row group, because its `.pillLayer` is
              `position: sticky; top: 0` and a sticky box can only ever be pushed DOWN from its
              natural flow position — never lifted above it. Rendered last (where it was) the
              pill's flow position is below every row: with 1760px of rows in a 600px scrollport it
              painted at y=1888 and was invisible at every scroll position, so the user got the
              guide LINE with no date on it. First puts its flow position at y=0, where `top: 0`
              pins it under the header at a constant height at every scroll position.

              Nothing else moves with it. `.line` is `position: absolute` and `.pillLayer` carries
              an explicit `z-index`, so neither's PAINT order depends on where in this list they
              sit — only the sticky box's flow position does. Both are `aria-hidden`, so putting
              them ahead of the row groups does not disturb the `grid`'s required children. */}
          <HoverGuide
            canvasRef={canvasRef}
            // N2 — BOTH refs, and they are not interchangeable. `canvasRef` is the coordinate
            // space the guide is DRAWN in; `scrollRef` is the coordinate space the sidebar dead
            // zone has to be MEASURED in, because the row-header column is sticky at this
            // element's left edge and not at the canvas origin. See `HoverGuideProps.scrollRef`.
            scrollRef={scrollRef}
            scale={state.scale}
            windowStartDay={state.range.startDay}
            sidebarWidth={SIDEBAR_PX}
          />

          {/* Both groups are declared here, where the grid is: `role="row"` is only valid
              under a `rowgroup` or a `grid`, and a `grid` whose owned `columnheader`s sit in no
              row of its own is an `aria-required-children` violation. `TimelineHeader` and
              `RowList` each own one row group; neither owns the grid.

              This wrapper is also the sticky box for the header — `.headerGroup` carries
              `position: sticky` and `background: inherit`, both of which stop working if they
              are declared on the header inside it. See `gantt-chart.module.scss`. */}
          <div className={styles['headerGroup']} role="rowgroup">
            <TimelineHeader
              scale={state.scale}
              range={state.range}
              window={dayWindow}
              todayDay={state.todayDay}
              workingWeek={state.workingWeek}
            />
          </div>

          {isTodayInRange ? (
            <div
              data-testid="today-line"
              className={styles['todayLine']}
              aria-hidden="true"
              // `todayX` is the LEFT EDGE of today's column, and the line centres itself on this
              // coordinate with `translateX(-50%)` — so without the half-column it straddles the
              // boundary between yesterday and today rather than marking today. The old guide got
              // this free by living inside the column at `left: 50%`.
              style={cssVars({
                '--today-x': `${SIDEBAR_PX + todayX + state.scale.pxPerDay / 2}px`,
              })}
            />
          ) : null}

          {/* The row-header column, continued past the last row to the bottom of the scrollport.
              Each row paints its own sidebar cell (`row-header.module.scss`), so a chart with
              fewer rows than the viewport is tall used to stop that column at the last row and let
              `.canvas`'s day grid — weekend stripes, week lines — run straight across the space
              underneath it, which read as the sidebar ending mid-air.

              An ABSOLUTE layer holding a STICKY child, and both halves are load-bearing. The
              layer is `.canvas`-sized, which is where "to the bottom" comes from: `.canvas` is
              `min-height: 100%` of the scrollport, so `top: 0; bottom: 0` reaches the bottom edge
              whether the rows fill it or not. The child is what stays at the LEFT edge — the
              column is sticky in every row above, so a plain absolute box at `left: 0` would sit
              at the canvas origin and slide away the moment the chart is scrolled. Sticky inside
              an absolutely positioned parent still resolves against the scrollport, and clamps to
              that parent, which is the full canvas width.

              `aria-hidden` and `pointer-events: none`: it is the column's background and nothing
              else. It sits at `z-index: -1` for the same reason `.todayLine` does — see there —
              and AFTER it in DOM order, so the today line is hidden behind the column below the
              last row exactly as it is behind the row headers above it. */}
          <div className={styles['sidebarFillLayer']} aria-hidden="true">
            <div className={styles['sidebarFill']} data-testid="sidebar-fill" />
          </div>

          <div role="rowgroup" data-testid="gantt-rows">
            <RowList
              rows={state.rows}
              scrollRef={scrollRef}
              scale={state.scale}
              windowStartDay={state.range.startDay}
              renderers={renderers}
              onToggleCollapsed={state.toggleCollapsed}
              scrollLeft={scrollLeft}
              viewportWidth={viewportWidth}
              sidebarWidth={SIDEBAR_PX}
              onScrollToRow={onScrollToRow}
              rowIndexOffset={HEADER_ROW_INDEX}
              readOnly={config?.readOnly}
              onCommit={onCommit}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
